<template>
  <!--
    AB#21824 — Create with ELSA. A conversation that DRAFTS the Create manual
    order form; it never creates. Every merchant, customer, address and product
    in the draft was resolved by the backend against the catalogue this viewer
    may order for — ELSA proposes names, code decides. Files go to the same
    temp folder the manual form uses, under this draft's own upload session, so
    "Review & create" opens the form with everything in place and a person
    presses Create there. Mirrors CreateWithAtlasDrawer; ELSA identity only.
  -->
  <Drawer form
    title="Create with ELSA"
    subtitle="Create an order · ELSA drafts, you create"
    width="560px"
    data-test="create-with-elsa-drawer"
    @close="emit('close')"
  >
    <template #title>
      <div class="agent-ident">
        <AgentAvatar
          agent="elsa"
          :size="44"
          :state="canReview ? 'done' : isThinking ? 'thinking' : 'idle'"
          pop
        />
        <div>
          <h2 class="dr-title" data-test="dr-title">Create with ELSA</h2>
          <span class="dr-sub">Create an order · ELSA drafts, you create</span>
        </div>
      </div>
    </template>

    <section class="agent-have" data-test="elsa-checklist" aria-label="What ELSA has so far">
      <div class="agent-have-h">
        <span>What ELSA has so far</span>
        <span class="agent-cnt" data-test="elsa-progress">{{ progress }}</span>
      </div>
      <div
        v-for="item in checklist"
        :key="item.key"
        class="agent-row"
        :data-test="`elsa-item-${item.key}`"
        :data-state="item.done ? 'ok' : 'needed'"
      >
        <span class="agent-st" :class="item.done ? 'agent-st--ok' : 'agent-st--needed'">{{
          item.done ? '✓' : '!'
        }}</span>
        <span class="agent-k">{{ item.label }}</span>
        <span class="agent-v" :class="{ 'agent-v--miss': !item.done }">{{ valueOf(item.key) }}</span>
      </div>
    </section>

    <section
      v-if="draft.products.length"
      class="eo-lines"
      data-test="elsa-lines"
      aria-label="Products drafted"
    >
      <div class="agent-have-h">
        <span>Products</span>
        <span class="agent-cnt">{{ draft.products.length }}</span>
      </div>
      <article
        v-for="(line, i) in draft.products"
        :key="i"
        class="eo-line"
        :data-test="`elsa-line-${i}`"
      >
        <div class="eo-line-h">
          <strong>{{
            line.is_custom_product ? line.new_product_name : line.product?.sku_id
          }}</strong>
          <span v-if="line.is_custom_product" class="eo-tag eo-tag--custom">custom product</span>
          <span v-else-if="line.product?.factory_name" class="eo-mute">{{
            line.product.factory_name
          }}</span>
        </div>
        <div class="eo-line-m">
          <span v-if="line.style">Style {{ line.style.name }}</span>
          <span v-if="line.addons.length"
            >Add-ons: {{ line.addons.map((a) => a.title).join(', ') }}</span
          >
          <span v-if="line.design_nick_name">Design “{{ line.design_nick_name }}”</span>
          <span v-if="line.design_id">Design ID {{ line.design_id }}</span>
        </div>
        <div v-if="line.roster.length" class="eo-sizes" :data-test="`elsa-sizes-${i}`">
          <span v-for="(q, s) in sizeTotals(line)" :key="s" class="eo-size"
            >{{ s }} <b>{{ q }}</b></span
          >
          <span class="eo-mute">· {{ lineTotal(line) }} pcs</span>
        </div>
        <p v-else class="eo-miss">No sizes or quantities yet</p>
        <label
          v-if="line.is_custom_product"
          class="eo-confirm"
          :data-test="`elsa-confirm-custom_product:${i}`"
        >
          <input
            type="checkbox"
            :checked="confirmed.has(`custom_product:${i}`)"
            @change="toggleConfirm(`custom_product:${i}`)"
          />
          <span>Not in the catalogue — I mean to order this as a custom product</span>
        </label>
        <div class="eo-files">
          <span
            v-for="f in filesFor(i)"
            :key="f.path"
            class="eo-file"
            :title="f.original_name || f.name"
          >
            {{ fileLabel(f.type) }}: {{ f.original_name || f.name }}
            <button
              type="button"
              class="eo-x"
              :aria-label="`Remove ${f.original_name || f.name}`"
              @click="removeFile(f.path)"
            >
              ×
            </button>
          </span>
          <span v-if="local.rosters[i]" class="eo-file"
            >Roster sheet: {{ local.rosters[i].name }}</span
          >
          <label class="eo-add">
            <input
              type="file"
              multiple
              :accept="ASSET_ACCEPT"
              hidden
              :disabled="isUploading"
              @change="onPick($event, 'assets', i)"
            />
            + Artwork
          </label>
          <label class="eo-add">
            <input
              type="file"
              :accept="IMAGE_ACCEPT"
              hidden
              :disabled="isUploading"
              @change="onPick($event, 'front_image', i)"
            />
            + Front image
          </label>
          <label class="eo-add">
            <input
              type="file"
              :accept="IMAGE_ACCEPT"
              hidden
              :disabled="isUploading"
              @change="onPick($event, 'back_image', i)"
            />
            + Back image
          </label>
          <label class="eo-add">
            <input type="file" accept=".xlsx" hidden @change="onPick($event, 'roster', i)" />
            + Roster sheet
          </label>
        </div>
      </article>
    </section>

    <div class="eo-order-files" data-test="elsa-order-files">
      <span v-if="local.po_document" class="eo-file"
        >PO document: {{ local.po_document.name }}</span
      >
      <label class="eo-add">
        <input type="file" :accept="PO_ACCEPT" hidden @change="onPick($event, 'po_document', 0)" />
        + PO document
      </label>
      <span v-if="isUploading" class="eo-mute" data-test="elsa-uploading">Uploading…</span>
    </div>

    <div
      v-if="problems.length || dropped.length"
      class="agent-problems"
      data-test="elsa-problems"
      role="status"
    >
      <p v-for="(p, i) in problems" :key="`p${i}`">{{ p }}</p>
      <p v-if="dropped.length" data-test="elsa-dropped">
        Left out — not found in the catalogue: {{ dropped.join(', ') }}.
      </p>
    </div>

    <div ref="log" class="agent-chat" data-test="elsa-chat" aria-live="polite">
      <div
        v-for="(m, i) in messages"
        :key="i"
        class="agent-msg"
        :class="m.role === 'user' ? 'agent-msg--u' : 'agent-msg--a'"
      >
        <AgentAvatar v-if="m.role === 'assistant'" agent="elsa" :size="28" />
        <span v-else class="agent-who">{{ initials }}</span>
        <div class="agent-b">{{ m.content }}</div>
      </div>
      <div v-if="isThinking" class="agent-msg agent-msg--a" data-test="elsa-thinking">
        <AgentAvatar agent="elsa" :size="28" state="thinking" />
        <div class="agent-b agent-b--thinking">ELSA is thinking…</div>
      </div>
      <p v-if="error" class="agent-error" data-test="elsa-error">{{ error }}</p>
    </div>

    <form class="agent-composer" @submit.prevent="submit">
      <input
        id="elsa-order-composer"
        ref="input"
        v-model="text"
        class="agent-input"
        data-test="elsa-input"
        placeholder="Describe the order, or reply to ELSA…"
        autocomplete="off"
        maxlength="2000"
        :disabled="isThinking"
      />
      <button
        type="submit"
        class="agent-send"
        data-test="elsa-send"
        aria-label="Send"
        :disabled="isThinking || !text.trim()"
      >
        <PaperAirplaneIcon class="size-4" />
      </button>
    </form>
    <div class="agent-fine">
      <span>ELSA only uses the merchant's own catalogue.</span>
      <span>Nothing is created from this chat.</span>
    </div>

    <template #footer>
      <span class="agent-hint" data-test="elsa-hint">{{ hint }}</span>
      <button
        type="button"
        class="dash-btn"
        data-test="cwe-manual"
        @click="emit('switch-to-manual')"
      >
        Create manually instead
      </button>
      <button
        type="button"
        class="dash-btn dash-btn--primary"
        data-test="elsa-review"
        :disabled="!canReview"
        @click="review"
      >
        Review &amp; create →
      </button>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { PaperAirplaneIcon } from '@heroicons/vue/24/outline';
import Drawer from '@/components/Shared/Drawer.vue';
import AgentAvatar from '@/components/Shared/AgentAvatar.vue';
import {
  useOrderAssistant,
  type DraftFile,
  type DraftFileType,
  type DraftProduct,
  type ElsaOrderPrefill,
} from '@/composables/useOrderAssistant';
import { readRosterSheet } from '@/utils/rosterSheet';

const ASSET_ACCEPT = '.png,.svg,.jpg,.jpeg,.pdf,.cdr,.tif,.tiff,.zip,.ai,.eps';
const IMAGE_ACCEPT = '.png,.jpg,.jpeg,.svg';
const PO_ACCEPT = '.pdf,.xls,.xlsx';

const props = withDefaults(defineProps<{ viewerName?: string | null }>(), { viewerName: null });

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'switch-to-manual'): void;
  /** The draft is ready and confirmed: open the Create manual order form prefilled with it. */
  (e: 'review', prefill: ElsaOrderPrefill): void;
}>();

const {
  messages,
  draft,
  checklist,
  problems,
  dropped,
  confirmed,
  unconfirmed,
  ready,
  canReview,
  isThinking,
  isUploading,
  error,
  local,
  prefill,
  send,
  addFiles,
  removeFile,
  toggleConfirm,
} = useOrderAssistant();

const text = ref('');
const input = ref<HTMLInputElement | null>(null);
const log = ref<HTMLElement | null>(null);

const initials = computed(() =>
  (props.viewerName || 'You')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
);

const doneCount = computed(() => checklist.value.filter((i) => i.done).length);
const progress = computed(() => `${doneCount.value} of ${checklist.value.length}`);
const hint = computed(() => {
  if (canReview.value) return 'Ready — check it on the form, then Create';
  if (ready.value && unconfirmed.value.length)
    return `Tick ${unconfirmed.value.length} custom product${unconfirmed.value.length === 1 ? '' : 's'} to continue`;
  const n = checklist.value.length - doneCount.value;
  return `${n} item${n === 1 ? '' : 's'} still needed`;
});

const valueOf = (key: string): string => {
  const d = draft.value;
  switch (key) {
    case 'merchant':
      return d.merchant?.name ?? 'needed';
    case 'customer':
      return d.customer ? `${d.customer.name}` : 'needed';
    case 'address':
      if (d.address) return d.address.label;
      if (d.custom_address)
        return (
          [d.custom_address.address1, d.custom_address.city, d.custom_address.country?.name]
            .filter(Boolean)
            .join(', ') + (d.custom_address.complete ? ' (new)' : ' — incomplete')
        );
      return 'needed';
    case 'order_type':
      return d.order_type.label;
    case 'products':
      return d.products.length ? `${d.products.length} drafted` : 'needed';
    case 'quantities': {
      const total = d.products.reduce((n, l) => n + lineTotal(l), 0);
      return total ? `${total} pcs` : 'needed';
    }
    default:
      return '';
  }
};

const sizeTotals = (line: DraftProduct): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const r of line.roster) out[r.size] = (out[r.size] ?? 0) + r.quantity;
  return out;
};
const lineTotal = (line: DraftProduct): number => line.roster.reduce((n, r) => n + r.quantity, 0);
const filesFor = (i: number): DraftFile[] =>
  draft.value.files.filter((f) => f.product_index === i && f.type !== 'po_document');
const fileLabel = (t: DraftFileType): string =>
  ({ assets: 'Artwork', front_image: 'Front', back_image: 'Back', po_document: 'PO' })[t];

const onPick = async (e: Event, type: DraftFileType | 'roster', i: number) => {
  const el = e.target as HTMLInputElement;
  const files = Array.from(el.files ?? []);
  el.value = '';
  if (!files.length) return;
  await addFiles(type, i, files, readRosterSheet);
};

const sendText = async (value: string) => {
  text.value = '';
  await send(value);
};
const submit = () => sendText(text.value);

const review = () => {
  if (canReview.value) emit('review', prefill.value);
};

const scrollLog = async () => {
  await nextTick();
  try {
    if (log.value) log.value.scrollTop = log.value.scrollHeight;
  } catch {
    /* no layout (tests) */
  }
};
watch(() => messages.value.length, scrollLog);
watch(isThinking, scrollLog);

onMounted(async () => {
  await nextTick();
  try {
    input.value?.focus();
  } catch {
    /* no document (tests) */
  }
});
</script>

<style lang="scss" scoped>
.agent-ident {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.agent-have {
  border: 1px solid var(--border);
  border-radius: 11px;
  background: var(--surface-2);
  padding: 10px 12px;
  margin-bottom: 12px;
}
.agent-have-h {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 6px;
}
.agent-cnt {
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  color: var(--ink-soft);
}
.agent-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 5px 0;
  font-size: 13px;
  border-top: 1px solid var(--border-soft);
}
.agent-row:first-of-type {
  border-top: 0;
}
.agent-k {
  width: 96px;
  flex: none;
  color: var(--ink-mute);
  font-size: 12px;
}
.agent-v {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--ink);
}
.agent-v--miss {
  color: var(--ink-mute);
  font-style: italic;
}
.agent-st {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  flex: none;
  font-size: 11px;
  font-weight: 800;
}
.agent-st--ok {
  background: var(--success-soft);
  color: var(--success);
}
.agent-st--needed {
  background: var(--warning-soft);
  color: var(--warning);
}
.agent-st--optional {
  background: var(--surface-3);
  color: var(--ink-mute);
}
.agent-problems {
  border: 1px solid var(--warning);
  border-radius: 10px;
  background: var(--warning-soft);
  color: var(--ink);
  font-size: 12.5px;
  padding: 8px 12px;
  margin-bottom: 12px;
}
.agent-problems p {
  margin: 2px 0;
}
.agent-chat {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 320px;
  overflow: auto;
  padding: 2px 0 6px;
}
.agent-msg {
  display: flex;
  gap: 9px;
  align-items: flex-start;
}
.agent-msg--u {
  flex-direction: row-reverse;
}
.agent-b {
  max-width: 86%;
  padding: 9px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.agent-msg--a .agent-b {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-top-left-radius: 4px;
}
.agent-msg--u .agent-b {
  background: var(--accent-soft);
  color: var(--ink);
  border-top-right-radius: 4px;
}
.agent-b--thinking {
  color: var(--ink-mute);
  font-style: italic;
}
.agent-who {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--surface-3);
  display: grid;
  place-items: center;
  font-size: 10px;
  font-weight: 800;
  color: var(--ink-soft);
  flex: none;
}
.agent-error {
  margin: 0;
  font-size: 12.5px;
  color: var(--danger);
}
.agent-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 4px 0 0 35px;
}
.agent-chip {
  font-size: 11.5px;
  padding: 4px 9px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-soft);
  cursor: pointer;
}
.agent-chip:hover:not(:disabled) {
  border-color: var(--accent);
  color: var(--ink);
}
.agent-chip:disabled {
  opacity: 0.5;
  cursor: default;
}
.agent-composer {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 10px;
  border: 1px solid var(--border);
  border-radius: 11px;
  background: var(--surface-2);
  padding: 6px 6px 6px 12px;
}
.agent-composer:focus-within {
  border-color: var(--accent);
}
.agent-input {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: 0;
  color: var(--ink);
  font-size: 13.5px;
  outline: 0;
}
.agent-send {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: var(--accent);
  color: #fff;
  display: grid;
  place-items: center;
  border: 0;
  cursor: pointer;
}
.agent-send:disabled {
  opacity: 0.5;
  cursor: default;
}
.agent-fine {
  font-size: 11px;
  color: var(--ink-mute);
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.agent-hint {
  margin-right: auto;
  font-size: 12px;
  line-height: 1.3;
  color: var(--ink-mute);
  min-width: 0;
  flex: 1 1 0;
  align-self: center;
}

.eo-lines {
  border: 1px solid var(--border);
  border-radius: 11px;
  background: var(--surface-2);
  padding: 10px 12px;
  margin-bottom: 12px;
}
.eo-line {
  padding: 8px 0;
  border-top: 1px solid var(--border-soft);
}
.eo-line:first-of-type {
  border-top: 0;
}
.eo-line-h {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13.5px;
  color: var(--ink);
}
.eo-line-m {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 12px;
  color: var(--ink-soft);
  margin-top: 2px;
}
.eo-sizes {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 6px;
  font-size: 12.5px;
  color: var(--ink-soft);
}
.eo-size {
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--surface-3);
  border: 1px solid var(--border);
}
.eo-size b {
  color: var(--ink);
}
.eo-miss {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--warning);
}
.eo-mute {
  color: var(--ink-mute);
  font-size: 12px;
}
.eo-tag {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 999px;
}
.eo-tag--custom {
  color: var(--warning);
  border: 1px solid var(--warning);
}
.eo-confirm {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 12.5px;
  color: var(--warning);
  font-weight: 600;
}
.eo-files,
.eo-order-files {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 6px;
}
.eo-order-files {
  margin-bottom: 12px;
}
.eo-file {
  font-size: 12px;
  color: var(--ink-soft);
  background: var(--surface-3);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 2px 8px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.eo-x {
  border: 0;
  background: none;
  color: var(--ink-mute);
  cursor: pointer;
  padding: 0 0 0 4px;
}
.eo-add {
  font-size: 12px;
  font-weight: 600;
  color: var(--accent);
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 6px;
}
.eo-add:hover {
  background: var(--surface-3);
}
</style>
